<template>
  <section id="karya-kami" class="gr-section gr-work">
    <div class="gr-container">
      <header class="gr-head gr-work__head">
        <div class="gr-work__head-copy">
          <p class="gr-eyebrow" aria-hidden="true">
            <span class="gr-eyebrow__index">03</span>
            <span class="gr-eyebrow__rule" />
          </p>
          <h2 class="gr-h2">Karya Kami untuk Bangsa</h2>
        </div>
        <!-- The single documentation CTA for the whole page, visible on both
             desktop and mobile, targeting the persistent anchor wrapper. -->
        <a href="#dokumentasi-kegiatan" class="gr-btn gr-btn--outline">
          Lihat Dokumentasi
          <v-icon icon="mdi-arrow-down" size="18" aria-hidden="true" />
        </a>
      </header>

      <ol class="gr-work__list">
        <li
          v-for="(item, index) in workItems"
          :key="item.title"
          class="gr-work__item"
        >
          <span class="gr-work__index" aria-hidden="true">
            {{ String(index + 1).padStart(2, "0") }}
          </span>
          <div class="gr-work__body">
            <h3 class="gr-work__title">{{ item.title }}</h3>
            <p class="gr-work__copy">{{ item.description }}</p>

            <!-- Canonical revealed these paragraphs on hover only, which put
                 them out of reach of keyboard and touch visitors. Here each is
                 a real disclosure. The panel element always exists so
                 aria-controls always resolves. -->
            <template v-if="item.details">
              <button
                type="button"
                class="gr-work__toggle"
                :aria-expanded="openIndexes.has(index)"
                :aria-controls="`gr-work-detail-${index}`"
                @click="toggle(index)"
              >
                <v-icon
                  :icon="
                    openIndexes.has(index)
                      ? 'mdi-minus-thick'
                      : 'mdi-plus-thick'
                  "
                  size="14"
                  aria-hidden="true"
                />
                {{ openIndexes.has(index) ? "Tutup" : "Selengkapnya" }}
              </button>
              <v-expand-transition>
                <div
                  v-show="openIndexes.has(index)"
                  :id="`gr-work-detail-${index}`"
                  class="gr-work__detail"
                >
                  <p class="gr-work__detail-copy">{{ item.details }}</p>
                </div>
              </v-expand-transition>
            </template>
          </div>
        </li>
      </ol>
    </div>
  </section>
</template>

<script lang="ts" setup>
// Copy carried over verbatim, including the two entries that intentionally
// carry no detail: spec 003 §5.4 removed their unsupported numeric claims and
// forbids any replacement claim.
const workItems = [
  {
    title: "Fasilitasi Kolaborasi",
    description:
      "Membangun platform yang menghubungkan individu dan komunitas untuk berkontribusi dalam berbagai kegiatan sosial.",
    details:
      "Mengembangkan ekosistem digital yang memungkinkan volunteer dan komunitas saling terhubung dengan mudah dan efisien.",
  },
  {
    title: "Dukung Inisiatif Sosial",
    description:
      "Menyediakan dukungan logistik dan sumber daya untuk kegiatan yang bermanfaat bagi masyarakat, seperti kebersihan tempat ibadah dan panti jompo.",
  },
  {
    title: "Berdayakan Pemuda",
    description:
      "Mendorong partisipasi aktif anak muda dalam aksi sosial, mengembangkan potensi dan kepemimpinan mereka.",
    details:
      "Kami menyelenggarakan pelatihan kepemimpinan dan manajemen proyek sosial untuk para relawan muda di berbagai daerah.",
  },
  {
    title: "Ciptakan Kesempatan Karier",
    description:
      "Menawarkan peluang karier dan pencarian bakat berdasarkan keterlibatan sosial, menciptakan jaringan profesional yang peduli.",
    details:
      "Menghubungkan relawan berprestasi dengan perusahaan yang mencari kandidat dengan semangat sosial dan kepemimpinan yang terbukti.",
  },
  {
    title: "Bangun Kesadaran Sosial",
    description:
      "Meningkatkan kesadaran masyarakat akan isu-isu sosial dan pentingnya solidaritas melalui edukasi dan kampanye.",
  },
  {
    title: "Program Warakawuri",
    description:
      "Memberikan dukungan dan kesempatan bagi Warakawuri untuk tetap aktif dan berkontribusi dalam masyarakat.",
    details:
      "Program ini ditujukan bagi para Warakawuri sebagai upaya mendorong keaktifan sosial di lingkungan masyarakat. Melalui kegiatan kebersamaan, pendampingan, dan partisipasi dalam berbagai kegiatan sosial, Warakawuri diharapkan dapat terus berperan aktif dan berkontribusi positif bagi masyarakat sekitar. Program ini menjadi wujud kepedulian Gotong Royong dalam memperkuat semangat inklusif, solidaritas, dan kemandirian sosial di kalangan keluarga pensiunan.",
  },
];

// Each entry opens and closes on its own; opening one never closes another.
const openIndexes = ref(new Set<number>());

const toggle = (index: number): void => {
  const next = new Set(openIndexes.value);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  openIndexes.value = next;
};
</script>

<style scoped>
.gr-work__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.25rem;
}

.gr-work__head-copy {
  flex: 1 1 320px;
  min-width: 0;
}

.gr-work__list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

@media (min-width: 960px) {
  .gr-work__list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 3rem;
  }
}

.gr-work__item {
  display: grid;
  grid-template-columns: 2.75rem minmax(0, 1fr);
  gap: 0 1rem;
  align-content: start;
  padding-block: 1.75rem;
  border-top: 1px solid var(--gr-line);
}

.gr-work__index {
  color: var(--gr-green);
  font-size: 0.8125rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.08em;
  line-height: 1.9;
}

.gr-work__body {
  min-width: 0;
}

.gr-work__title {
  margin: 0 0 0.5rem;
  color: var(--gr-ink);
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.35;
}

.gr-work__copy {
  max-width: 52ch;
  margin: 0;
  color: var(--gr-ink-2);
  font-size: 1rem;
  line-height: 1.7;
}

.gr-work__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  margin-top: 0.5rem;
  padding: 0;
  background: none;
  border: none;
  color: var(--gr-green-deep);
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
  cursor: pointer;
}

@media (hover: hover) {
  .gr-work__toggle:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

.gr-work__detail {
  border-left: 2px solid var(--gr-green-tint);
  padding-left: 1rem;
}

.gr-work__detail-copy {
  max-width: 52ch;
  margin: 0 0 0.25rem;
  color: var(--gr-ink-3);
  font-size: 0.9375rem;
  line-height: 1.7;
}

@media (max-width: 320px) {
  .gr-work__item {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  .gr-work__index {
    line-height: 1.4;
  }
}
</style>
